import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  CustomerDocumentTypeEnum,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getCustomerHistoryFile } from 'redux/reducers/clientSlice';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import { getYesNoAnswer } from '../../../../../utils';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  customerId: string;
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function GeneralContractInfoDetailsCard(props: Props) {
  const { historyDetails, customerId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  // General Contract Information
  const hasInsuranceRequirementsChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements,
  );
  const hasCertificateOfInsuranceSpecificationsChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.certificateOfInsuranceSpecifications,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.certificateOfInsuranceSpecifications,
  );
  const hasIsCreditCheckVerifiedChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.isCreditCheckVerified,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.isCreditCheckVerified,
  );
  const hasSubmittedToChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.submittedTo,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.submittedTo,
  );
  const hasCertificateOfInsuranceFileChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.certificateOfInsuranceCreatedFileKey,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.certificateOfInsuranceCreatedFileKey,
  );
  const hasSowRequiredChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.sowRequired,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.sowRequired,
  );
  const hasSowSpecialInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.sowSpecialInstructions,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.sowSpecialInstructions,
  );
  const hasSowTemplateFileKeyChanged = !isEqual(
    oldData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.sowTemplateFileKey,
    newData?.additionalInfo?.generalContractInformation?.insuranceRequirements
      ?.sowTemplateFileKey,
  );

  const handleCertificateOfInsuranceFileDownload = async (fileKey?: string) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType: CustomerDocumentTypeEnum.CertificateOfInsurance,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleFileDownload = async (
    fileKey: string,
    documentType: CustomerDocumentTypeEnum,
  ) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderInsuranceRequirementsSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'insurance-requirements';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Insurance requirements</div>
          </Col>

          {hasCertificateOfInsuranceSpecificationsChanged && (
            <HistoryField
              title="Certificate of insurance specifications"
              value={
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements?.certificateOfInsuranceSpecifications
              }
              dataId={`${sectionName}-certificate-insurance-specifications-field`}
            />
          )}

          {hasSubmittedToChanged && (
            <HistoryField
              title="Submitted to"
              value={
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements?.submittedTo
              }
              dataId={`${sectionName}-submitted-to-field`}
            />
          )}

          {hasCertificateOfInsuranceFileChanged && (
            <HistoryField
              title="Certificate of insurance created"
              emptyText="No file"
              value={
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements
                  ?.certificateOfInsuranceCreatedFileKey &&
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements
                  ?.certificateOfInsuranceCreatedFileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleCertificateOfInsuranceFileDownload(
                        customerDetails?.additionalInfo
                          ?.generalContractInformation?.insuranceRequirements
                          ?.certificateOfInsuranceCreatedFileKey,
                      )
                    }
                  >
                    {getFileNameWithoutExt(
                      customerDetails?.additionalInfo
                        ?.generalContractInformation?.insuranceRequirements
                        ?.certificateOfInsuranceCreatedFileName,
                    )}
                  </span>
                )
              }
              dataId={`${sectionName}-certificate-insurance-created-field`}
            />
          )}

          {hasIsCreditCheckVerifiedChanged && (
            <HistoryField
              title="Credit check verified"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements?.isCreditCheckVerified,
              )}
              dataId={`${sectionName}-credit-check-verified-field`}
            />
          )}

          {hasSowRequiredChanged && (
            <HistoryField
              title="SOW required"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements?.sowRequired,
              )}
              dataId={`${sectionName}-SOW-required-field`}
            />
          )}

          {hasSowSpecialInstructionsChanged && (
            <HistoryField
              title="SOW special instructions"
              value={
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements?.sowSpecialInstructions
              }
              dataId={`${sectionName}-SOW-special-instructions-field`}
            />
          )}

          {hasSowTemplateFileKeyChanged && (
            <HistoryField
              title="SOW template"
              emptyText="No file"
              value={
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements?.sowTemplateFileKey &&
                customerDetails?.additionalInfo?.generalContractInformation
                  ?.insuranceRequirements?.sowTemplateFileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleFileDownload(
                        customerDetails?.additionalInfo
                          ?.generalContractInformation?.insuranceRequirements
                          ?.sowTemplateFileKey as string,
                        CustomerDocumentTypeEnum.SowTemplate,
                      )
                    }
                  >
                    {getFileNameWithoutExt(
                      customerDetails?.additionalInfo
                        ?.generalContractInformation?.insuranceRequirements
                        ?.sowTemplateFileName,
                    )}
                  </span>
                )
              }
              dataId={`${sectionName}-SOW-template-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderAdditionalInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasInsuranceRequirementsChanged &&
          renderInsuranceRequirementsSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAdditionalInformation(oldData)}
      newData={renderAdditionalInformation(newData)}
    />
  );
}
